import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useSession } from '../lib/session'
import { toTitleCase } from '../lib/format'
import { artGradient } from '../lib/songColour'
import { Shell } from '../components/Shell'
import { Spinner, initials, avatarTone } from '../components/ui'

const STATE_NAMES = {
  NSW: 'New South Wales', VIC: 'Victoria', QLD: 'Queensland', WA: 'Western Australia',
  SA: 'South Australia', TAS: 'Tasmania', ACT: 'Australian Capital Territory', NT: 'Northern Territory',
}

/**
 * A church's own page — the outward-facing half of a church, as against the
 * roster and the review process, which stay behind /admin.
 *
 * Everything on it is curated by an admin at /admin/profile. Nothing is
 * inferred: a church that has never opened the editor shows nothing rather
 * than an auto-generated list of its members' work, because "we back these
 * writers" is a claim a person makes, not one a query does.
 *
 * The lists are RLS-shaped, same as WriterProfile — a featured song the
 * visitor can't reach simply doesn't come back, and the row is dropped. No
 * client-side filter here is load-bearing.
 */
const writerName = (w) => [w?.first_name, w?.last_name].filter(Boolean).join(' ')

/** Hostname only, for the label under a link card. Wrapped because a stored
 *  URL that somehow isn't parseable must not blank the whole page. */
const hostOf = (url) => {
  try { return new URL(url).hostname.replace(/^www\./, '') } catch { return 'Link' }
}

export default function ChurchProfile() {
  const { id } = useParams()
  const { approved } = useSession()
  const mine = approved?.some((m) => m.church_id === id && m.role === 'admin')

  const [church, setChurch] = useState(null)
  const [songs, setSongs] = useState([])
  const [writers, setWriters] = useState([])
  const [links, setLinks] = useState([])
  const [backing, setBacking] = useState(null)
  const [ready, setReady] = useState(false)

  const load = useCallback(async () => {
    const [{ data: c }, { data: feats }, { data: count }] = await Promise.all([
      supabase.from('church_profiles').select('*').eq('id', id).maybeSingle(),
      supabase.from('church_features').select('*').eq('church_id', id)
        .order('position', { ascending: true }).order('created_at', { ascending: true }),
      supabase.rpc('church_verified_writer_count', { p_church: id }),
    ])
    setChurch(c ?? null)
    setBacking(typeof count === 'number' ? count : null)

    const rows = feats ?? []
    setLinks(rows.filter((f) => f.kind === 'link'))

    const songIds = rows.filter((f) => f.kind === 'song').map((f) => f.song_id)
    const writerIds = rows.filter((f) => f.kind === 'writer').map((f) => f.writer_id)

    const [{ data: sg }, { data: wr }] = await Promise.all([
      songIds.length
        ? supabase.from('songs').select('id, title, scripture_refs').in('id', songIds)
        : Promise.resolve({ data: [] }),
      writerIds.length
        ? supabase.from('songwriter_profiles')
            .select('id, first_name, last_name, tagline, avatar_url').in('id', writerIds)
        : Promise.resolve({ data: [] }),
    ])

    // Keep the pastor's order, drop whatever this visitor can't see.
    const byId = (list) => Object.fromEntries((list ?? []).map((x) => [x.id, x]))
    const songMap = byId(sg)
    const writerMap = byId(wr)
    setSongs(rows.filter((f) => f.kind === 'song')
      .map((f) => ({ ...songMap[f.song_id], note: f.note })).filter((s) => s.id))
    setWriters(rows.filter((f) => f.kind === 'writer')
      .map((f) => ({ ...writerMap[f.writer_id], note: f.note })).filter((w) => w.id))
    setReady(true)
  }, [id])

  useEffect(() => { load() }, [load])

  if (!ready) return <Shell back="/collective" tabs><Spinner /></Shell>

  if (!church) {
    return (
      <Shell back="/collective" tabs>
        <h2 className="q">Can't find this church.</h2>
        <p className="sub">They may not have opened their page up yet.</p>
      </Shell>
    )
  }

  const where = [church.city, STATE_NAMES[church.state] || church.state].filter(Boolean).join(', ')
  const nothingYet = !songs.length && !writers.length && !links.length

  return (
    <Shell back="/collective" tabs>
      <div className="wp-head">
        <div className="ch-crest" style={{ background: artGradient(church.id) }}>
          {initials(church.name || 'Church')}
        </div>
        <h2 className="wp-name">
          {church.name}
          {church.verified && <span className="ch-tick" title="Verified church"> ✓</span>}
        </h2>
        {church.tagline && <p className="wp-tagline">{church.tagline}</p>}
        {(where || church.denomination) && (
          <p className="small" style={{ marginTop: 2 }}>
            {[where, church.denomination].filter(Boolean).join(' · ')}
          </p>
        )}
        {church.website_url && (
          <a className="ch-site" href={church.website_url} target="_blank" rel="noopener noreferrer">
            {hostOf(church.website_url)} ↗
          </a>
        )}
        {mine && (
          <Link className="btn-plain" style={{ padding: 0, marginTop: 10 }} to="/admin/profile">
            Edit your page ›
          </Link>
        )}
        {church.bio && <p className="small wp-bio">{church.bio}</p>}
      </div>

      <div className="wp-stats">
        <div className="wp-stat as-text"><b>{songs.length}</b><span>Songs</span></div>
        <div className="wp-stat as-text"><b>{writers.length}</b><span>Writers</span></div>
        <div className="wp-stat as-text">
          <b>{backing ?? '—'}</b><span>Vouched for</span>
        </div>
      </div>

      {nothingYet && (
        <p className="small">
          {mine
            ? 'Nothing on your page yet. Add a song, a writer, or a link to a Sunday recording.'
            : 'This church hasn’t put anything on their page yet.'}
        </p>
      )}

      {songs.length > 0 && (
        <>
          <h3 className="sechead">Songs from our people</h3>
          <div className="songcards">
            {songs.map((s) => (
              <Link key={s.id} className="songcard" to={`/collective/${s.id}`}
                    style={{ background: artGradient(s.id) }}>
                <b>{toTitleCase(s.title) || 'Untitled'}</b>
                {s.note
                  ? <small>{s.note}</small>
                  : s.scripture_refs?.length > 0 && <small>{s.scripture_refs.join(' · ')}</small>}
              </Link>
            ))}
          </div>
        </>
      )}

      {writers.length > 0 && (
        <>
          <h3 className="sechead">
            Writers here
            <span className="note">Their profiles, not ours — tap through and listen.</span>
          </h3>
          <div className="rows">
            {writers.map((w) => (
              <Link key={w.id} className="action" to={`/writer/${w.id}`}>
                <span className="ch-writer">
                  {w.avatar_url
                    ? <img className="wp-avatar" src={w.avatar_url} alt="" />
                    : <span className={`wp-avatar ${avatarTone(w.id)}`}>{initials(writerName(w))}</span>}
                  <span>
                    {writerName(w) || 'Writer'}
                    {(w.note || w.tagline) && <small>{w.note || w.tagline}</small>}
                  </span>
                </span>
                <span className="ar">›</span>
              </Link>
            ))}
          </div>
        </>
      )}

      {links.length > 0 && (
        <>
          <h3 className="sechead">
            Watch
            <span className="note">Hosted by the church, opens in a new tab.</span>
          </h3>
          <div className="rows">
            {links.map((l) => (
              <a key={l.id} className="action" href={l.url}
                 target="_blank" rel="noopener noreferrer">
                <span>
                  {l.title}
                  <small>{l.note ? `${l.note} · ${hostOf(l.url)}` : hostOf(l.url)}</small>
                </span>
                <span className="ar">↗</span>
              </a>
            ))}
          </div>
        </>
      )}
    </Shell>
  )
}
